import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import BiotechDirectory from '@/components/BiotechDirectory'
import CalculatorSuite, { type CalcTab } from '@/components/CalculatorSuite'
import Footer from '@/components/Footer'
import Header, { type TabId } from '@/components/Header'
import HomePanel from '@/components/HomePanel'
import LegalPanel from '@/components/LegalPanel'

const CALC_TITLES: Record<CalcTab, string> = {
  molarity: 'Molarity & Dilution Calculator (C1V1 = C2V2)',
  doubling: 'Cell Doubling Time Calculator',
  qpcr: 'qPCR DNA Copy Number Calculator',
}

const TAB_TITLES: Record<TabId, string> = {
  home: 'Home / Educational Pillars',
  calculators: 'Lab Calculator Suite',
  directory: 'Biotech Directory',
  legal: 'Regulatory & Legal Information',
}

function isCalcTab(value: string | undefined): value is CalcTab {
  return value === 'molarity' || value === 'doubling' || value === 'qpcr'
}

function getActiveTabFromPath(pathname: string): TabId {
  if (pathname.startsWith('/calculators'))
    return 'calculators'
  if (pathname.startsWith('/directory'))
    return 'directory'
  if (pathname.startsWith('/legal'))
    return 'legal'
  return 'home'
}

function Home() {
  const location = useLocation()
  const navigate = useNavigate()
  const { calcId } = useParams<{ calcId?: string }>()

  const activeTab = getActiveTabFromPath(location.pathname)
  const activeCalc: CalcTab = isCalcTab(calcId) ? calcId : 'molarity'

  // Deep-link guard: redirect unknown /calculators/:calcId values to the default calculator.
  useEffect(() => {
    if (activeTab === 'calculators' && calcId && !isCalcTab(calcId)) {
      navigate('/calculators/molarity', { replace: true })
    }
  }, [activeTab, calcId, navigate])

  // Keep the document title in sync with the deep-linked tab/calculator for SEO.
  useEffect(() => {
    const suffix = activeTab === 'calculators' ? CALC_TITLES[activeCalc] : TAB_TITLES[activeTab]
    document.title = `${suffix} | BioSlick Lab Calculators`
  }, [activeTab, activeCalc])

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header activeTab={activeTab} />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div
          id="panel-home"
          role="tabpanel"
          aria-labelledby="tab-home"
          hidden={activeTab !== 'home'}
        >
          <HomePanel />
        </div>
        <div
          id="panel-calculators"
          role="tabpanel"
          aria-labelledby="tab-calculators"
          hidden={activeTab !== 'calculators'}
        >
          <CalculatorSuite activeCalc={activeCalc} />
        </div>
        <div
          id="panel-directory"
          role="tabpanel"
          aria-labelledby="tab-directory"
          hidden={activeTab !== 'directory'}
        >
          <BiotechDirectory />
        </div>
        <div
          id="panel-legal"
          role="tabpanel"
          aria-labelledby="tab-legal"
          hidden={activeTab !== 'legal'}
        >
          <LegalPanel />
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Home
